import { useCallback, useMemo } from "react";
import useSWR, { cache, mutate } from "swr";

import { LightSpace, ProductType } from ".";
import { useAuth0 } from "../core/auth0";
import { WeekDay } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import { getDurationInHours } from "../offering/time_utils";
import { Booking, BookingType } from "./booking";
import { ImageData, ImageVersion } from "./image";
import { Location } from "./location";
import { Order, OrderableType, OrderItem, PaymentMethod } from "./orders";
import {
  GeoMarket,
  MembershipDetail,
  Partner,
  PartnerOffering,
  PartnerOfferingLocation,
  Products,
  LightPartnerOffering,
} from "./partner_offering";
import { Quote, QuoteOrderItem } from "./quotes";
import {
  Amenity,
  BookingHour,
  DynamicPriceRate,
  Pricing,
  PricingType,
  Space,
  SpaceBookingPolicy,
  SpaceLayout,
  toPriceRanges,
} from "./space";

export class APIError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
  }
}

export class UnauthorizedAPIError extends APIError {}

export function toAmenity(data: any): Amenity {
  return {
    id: data.id,
    name: data.name,
    iconURL: data.icon_url,
  };
}

function toPricing(data: any): Pricing {
  return {
    id: data.id,
    type:
      data.pricing_type === "hourly" ? PricingType.Hourly : PricingType.Daily,
    price: Number(data.price),
  };
}

function toSpaceLayout(data: any): SpaceLayout {
  return {
    id: data.id,
    name: data.name,
    imageURL: data.image_url,
    capacity: data.capacity,
    description: data.description,
    spaceID: data.space_id,
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
  };
}

function toBookingHour(data: any): BookingHour {
  let dayOfWeek = WeekDay.Monday;

  switch (data.day_of_week) {
    case "Monday":
      dayOfWeek = WeekDay.Monday;
      break;
    case "Tuesday":
      dayOfWeek = WeekDay.Tuesday;
      break;
    case "Wednesday":
      dayOfWeek = WeekDay.Wednesday;
      break;
    case "Thursday":
      dayOfWeek = WeekDay.Thursday;
      break;
    case "Friday":
      dayOfWeek = WeekDay.Friday;
      break;
    case "Saturday":
      dayOfWeek = WeekDay.Saturday;
      break;
    case "Sunday":
      dayOfWeek = WeekDay.Sunday;
      break;
    default:
      break;
  }
  return {
    dayOfWeek,
    openAt: data.open_at,
    closedAt: data.closed_at,
  };
}

export function toSpaceBookingPolicy(data: any): SpaceBookingPolicy {
  return {
    askForTax: data.ask_for_tax_number,
    requireTaxId: data.require_tax_id,
    minDuration: data.min_duration_hours,
    cancellationHours: data.cancellation_hours,
    cancellationDays: data.cancellation_days,
    cancellationTime1DayBefore: data.cancellation_time_1_day_before,
    disclaimer: data.disclaimer,
    checkoutWarning: data.checkout_warning,
    marketingConsent: data.marketing_consent,
    marketingConsentCheckedByDefault: data.marketing_consent_checked_by_default,
    terms: data.terms,
    askForBookingReason: data.ask_for_booking_reason,
    askForNumberOfGuests: data.ask_for_number_of_guests,
    cancellationWarning: data.cancellation_warning,
  };
}

export function toSpace(data: any): Space {
  return {
    id: data.id,
    spaceType: data.space_type,
    name: data.name,
    description: data.description,
    inventoryCapacity: data.inventory_capacity,
    currencyCode: data.currency || data.currency_code || null,
    defaultLayoutID: data.default_space_layout_id,
    images: data.images.map(toImage),
    layouts: data.space_layouts.map(toSpaceLayout),
    city: data.city,
    status: data.active ? "active" : "inactive",
    country: data.country,
    streetAddress: data.street_address,
    squareFootage: data.square_footage,
    attributes: {
      floor: data.floor,
      withWindow: data.with_window,
    },
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
    bookingHours: data.booking_hours.map(toBookingHour),
    bookingHour: data.booking_hour
      ? toBookingHour(data.booking_hour)
      : undefined,
    bookingPolicy: toSpaceBookingPolicy(data.booking_policy),
    locationID: data.location_id,
    locationTimeZone: data.location_time_zone,
    productType: toProductType(data.product_type),
    partnerID: data.partner_id,
    dynamicPriceRates: data.dynamic_price_rates.map(toDynamicPriceRate),
  };
}

export function toLightSpace(data: any): LightSpace {
  return {
    id: data.id,
    name: data.name,
    currencyCode: data.currency || data.currency_code || null,
    images: data.images.map(toImage),
    layouts: data.space_layouts.map(toSpaceLayout),
    streetAddress: data.street_address,
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
    dynamicPriceRates: data.dynamic_price_rates.map(toDynamicPriceRate),
    bookingHour: data.booking_hour
      ? toBookingHour(data.booking_hour)
      : undefined,
  };
}

function toDynamicPriceRate(data: any): DynamicPriceRate | undefined {
  if (data) {
    return {
      bookingCriteria: data.booking_criteria.map((bC: any) => ({
        minBookingUnit: bC.min_booking_unit
          ? Number(bC.min_booking_unit)
          : undefined,
        bookingType: bC.booking_type,
      })),
      dynamicPriceFormula: {
        adjustmentType: data.dynamic_price_formula.adjustment_type,
        adjustmentValue: Math.abs(
          Number(data.dynamic_price_formula.adjustment_value)
        ),
      },
    };
  }
}

function toProductType(data: any): ProductType | null {
  if (!data) {
    return null;
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    invites: data.invites,
    selectQuantity: data.select_quantity,
  };
}

function toImage(data: any): ImageVersion {
  return {
    original: toImageData(data.original),
    thumb: toImageData(data.thumb),
    small: toImageData(data.small),
    medium: toImageData(data.medium),
    large: toImageData(data.large),
    xlarge: toImageData(data.xlarge),
  };
}

function toImageData(data: any): ImageData {
  return {
    url: data.url,
    width: data.width,
    height: data.height,
  };
}

export function toLocation(data: any): Location {
  return {
    id: data.id,
    maxCapacity: data.max_capacity,
    name: data.name,
    logo: data.logo ? toImageData(data.logo) : null,
    streetAddress: data.street_address,
    city: data.city,
    timezone: data.time_zone,
    state: data.state,
    stateName: data.state_name,
    wlHomeLink: data.wl_home_link,
    postalCode: data.postal_code,
    currencyCode: data.currency || null,
    country: data.country,
    images: data.images?.map(toImage),
    direction: data.direction,
    longitude: Number(data.longitude),
    latitude: Number(data.latitude),
    pricingInclusion: data.pricing_inclusion,
    locationDisplay: data.location_display,
    googleAction: data.google_action
      ? toGoogleAction(data.google_action)
      : undefined,
  };
}

function toGoogleAction(data: any): Location["googleAction"] {
  return {
    entityId: data.entity_id,
    name: data.name,
    active: data.active,
  };
}

export function useGetLocation(locationID: string): Location {
  const response = useSWR(`/api/v2/locations/${locationID}`, {
    suspense: true,
  });

  return toLocation(response.data);
}

interface PurchaseStatusResult {
  has_portal_account: boolean;
  purchased_membership_portal: boolean;
  purchased_membership_wo_portal: boolean;
  purchased_multi_pass: boolean;
}

export interface PurchaseStatus {
  hasPortalAccount: boolean;
  purchasedMembershipPortal: boolean;
  purchasedMembershipWoPortal: boolean;
  purchasedMultiPass: boolean;
}

export interface PartnerOfferingParams {
  city?: string;
  state?: string;
  locationID?: string;
  locationIDs?: string[];
  minCapacity?: number;
  maxCapacity?: number;
  startDate?: string;
  endDate?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  layoutId?: string;
  productCategory?: string;
}

export function useGetPartnerOffering(
  offeringID: string,
  params?: PartnerOfferingParams
): PartnerOffering {
  let searchParams = new URLSearchParams();
  let qs = "";
  const { locale } = useI18n();

  if (params) {
    if (params.state) {
      searchParams.set("state", params.state);
    }
    if (params.city) {
      searchParams.set("city", params.city);
    }
    if (params.locationID) {
      searchParams.set("location_id", params.locationID);
    }
    if (params.locationIDs) {
      searchParams.set("location_ids", String(params.locationIDs));
    }
    if (params.minCapacity) {
      searchParams.set("min_capacity", String(params.minCapacity));
    }
    if (params.maxCapacity) {
      searchParams.set("max_capacity", String(params.maxCapacity));
    }
    if (params.startDate) {
      searchParams.set("start_date", params.startDate);
    }
    if (params.endDate) {
      searchParams.set("end_date", params.endDate);
    }
    if (params.date) {
      searchParams.set("date", params.date);
    }
    if (params.startTime) {
      searchParams.set("start_time", params.startTime);
      if (params.endTime) {
        searchParams.set("end_time", params.endTime);
      }
    }
    if (params.layoutId) {
      searchParams.set("layout_id", params.layoutId);
    }
    if (params.productCategory) {
      searchParams.set("product_category", params.productCategory);
    }

    qs = `?${searchParams.toString()}`;
  }

  if (qs) {
    qs += `&locale=${locale.languageTag}`;
  } else {
    qs += `?locale=${locale.languageTag}`;
  }

  const response = useSWR(`/api/v2/partner_offerings/${offeringID}${qs}`, {
    suspense: true,
    revalidateOnFocus: false,
  });

  return useMemo(() => toPartnerOffering(response.data), [response.data]);
}

export function useGetLightPartnerOffering(
  offeringID: string
): LightPartnerOffering {
  let searchParams = new URLSearchParams();
  let qs: string;
  const { locale } = useI18n();
  searchParams.set("light_response", "true");
  searchParams.set("locale", locale.languageTag);
  qs = `?${searchParams.toString()}`;

  const response = useSWR(`/api/v2/partner_offerings/${offeringID}${qs}`, {
    suspense: true,
    revalidateOnFocus: false,
  });

  return useMemo(() => toLightPartnerOffering(response.data), [response.data]);
}

export function usePartnerOfferingFMQuery(
  partnerID?: string
): PartnerOffering | null {
  const response = useSWR(
    partnerID ? `/api/v2/partner_offerings/${partnerID}` : null,
    {
      suspense: true,
      revalidateOnFocus: false,
    }
  );

  return useMemo(
    () => (response.data ? toPartnerOffering(response.data) : null),
    [response.data]
  );
}

export function useProductsByProductTypeQuery(
  productTypeID: string,
  partnerID: string
) {
  const response = useSWR(
    `/api/v2/partners/${partnerID}/product_type/${productTypeID}/products`
  );

  const data = useMemo(
    () => (response.data ? toProducts(response.data) : null),
    [response.data]
  );

  return {
    data,
    loading: partnerID && productTypeID && !response.data,
  };
}

function toPartnerOffering(data: any): PartnerOffering {
  return {
    id: data.id,
    name: data.name,
    offeringTitle: data.offering_title,
    template: data.template,
    description: data.description,
    addOns: data.add_ons.map(toMultiPassAddOn),
    partner: toPartner(data.partner),
    templateName: data.template_name,
    offeringLocations: data.offering_locations.map(toOfferingLocation),
    promoCodeBoxContent: data.promo_code_box_content,
    generalInfo: data.general_info,
    showDiscountLabel: data.show_discount_label,
    faqs: data.faqs,
    slug: data.slug,
    cancellationPolicy: data.cancellation_policy,
    usePartnerOfferingImagesAcrossLocations:
      data.use_partner_offering_images_across_locations,
    allowSelectableStartDate: data.allow_selectable_start_date,
    singleDaySelect: data.single_day_select,
  };
}

function toLightPartnerOffering(data: any): LightPartnerOffering {
  return {
    id: data.id,
    name: data.name,
    offeringTitle: data.offering_title,
    template: data.template,
    description: data.description,
    partner: toPartner(data.partner),
    promoCodeBoxContent: data.promo_code_box_content,
    generalInfo: data.general_info,
    showDiscountLabel: data.show_discount_label,
    faqs: data.faqs,
    slug: data.slug,
  };
}

function toPartner(data: any): Partner {
  return {
    id: data.id,
    name: data.name,
    partnerSlug: data.partner_slug,
    logoUrl: data.logo_url,
    homepageUrl: data.homepage_url,
    internalName: data.internal_name,
  };
}

function toProducts(data: any): Products {
  return {
    partner: data.partner,
    spaceLocations: data.space_locations.map(toOfferingLocation),
    addOns: data.add_ons ? data.add_ons.map(toMultiPassAddOn) : [],
    templateName: data.template_name,
  };
}

function toMultiPassAddOn(data: any) {
  return {
    id: data.id,
    addOnID: data.id,
    name: data.name,
    description: data.description,
    numberOfEntries: data.number_of_entries,
    daysToExpire: data.days_to_expire,
    sortOrder: data.sort_order,
    credits: data.meta_data?.credits,
    productCategory: data.product_category,
    productTypeName: data.product_type_name,
  };
}

function toOfferingLocation(data: any): PartnerOfferingLocation {
  return {
    id: data.id,
    spaceID: data.space_id,
    integrationProductID: data.integration_product_id,
    locationDisplay: data.location_display,
    location: {
      ...(data?.location || {}),
      city: data.location?.city,
      stateName: data.location?.state_name,
      currencyCode: data.location?.currency,
      streetAddress: data.location?.street_address,
    },
    offeringItemType: data.offering_item_type,
    membership: data.membership
      ? toOfferingMembership(data.membership)
      : undefined,
    promoCodeBoxContent: data.promo_code_box_content,
    featuredPromoDescription: data.featured_promo_description,
    generalInfo: data.general_info,
    faqs: data.faqs,
    spaceMaxCapacity: data.space_max_capacity,
  };
}

export function isPartnerLocation(
  locatable?: Location | GeoMarket
): locatable is Location {
  if (!locatable) return false;
  return (locatable as Location).id !== undefined;
}

const toLocatableLocation = (locatable: any): Location => ({
  ...(locatable || {}),
  city: locatable?.city,
  currencyCode: locatable?.currency,
  streetAddress: locatable?.street_address,
  latitude: Number(locatable.latitude),
  longitude: Number(locatable.longitude),
});

function toOfferingMembership(data: any): MembershipDetail {
  let locatable: Location | GeoMarket | undefined;

  if (isPartnerLocation(data.locatable)) {
    locatable = toLocatableLocation(data.locatable);
  } else if (!data.locatable) {
    locatable = undefined;
  } else {
    locatable = {
      id: data.locatable.id,
      name: data.locatable.name,
      description: data.locatable.description!,
      locations: data.locatable.locations.map(toLocatableLocation),
    };
  }

  return {
    currency: data.currency,
    description: data.description,
    id: data.id,
    locatable,
    locatableType: data.locatable_type,
    monthlyPrice: Number(data.monthly_price),
    name: data.name,
    partnerId: data.partner_id,
    stripeProductId: data.stripe_product_id,
    timeZone: data.time_zone,
    yearlyPrice: Number(data.yearly_price),
    firstPaymentCycles: data.first_payment_cycles,
    images: data.images,
    inventory: data.inventory,
    productCategory: data.product_category,
    productTypeName: data.product_type_name,
    recurring: data.recurring,
  };
}

export function useGetSpace(spaceID: string): Space {
  const response = useSWR(`/api/v2/spaces/${spaceID}`, { suspense: true });

  return toSpace(response.data);
}

export function useMaybeGetSpace(spaceID: string | null): Space | null {
  const response = useSWR(
    () => (spaceID ? `/api/v2/spaces/${spaceID}` : null),
    {
      suspense: true,
    }
  );
  if (!response.data) {
    return null;
  }
  return toSpace(response.data);
}

export type AvailabilityType =
  | "buffered"
  | "booked"
  | "available"
  | "blocked_out"
  | "not_enough_min_booking_hours"
  | "past_available"
  | "lead_time";

export interface AvailabilitySlot {
  from: string;
  to: string;
  available: boolean;
  type: AvailabilityType;
}

interface PhysicalSpaceCalendar {
  physicalSpaceID: string;
  availabilities: AvailabilitySlot[];
}

export interface SpaceCalendar {
  physicalSpaces: PhysicalSpaceCalendar[];
  open: boolean;
  date: string;
  spaceID: string;
}

interface SpaceCalendarParam {
  date: string;
}

function toSpaceCalendarSearchParams(
  spaceID: string,
  param: SpaceCalendarParam
) {
  const searchParams = new URLSearchParams();

  searchParams.set("space_id", spaceID);
  searchParams.set("date", param.date);

  return `?${searchParams.toString()}`;
}

function toSpaceCalendar(data: any): SpaceCalendar {
  return {
    date: data.date,
    open: data.open,
    spaceID: data.space_id,
    physicalSpaces: data.data.map(
      (data: any): PhysicalSpaceCalendar => ({
        physicalSpaceID: data.physical_space_id,
        availabilities: data.data,
      })
    ),
  };
}

export function useGetSpaceCalendar(
  spaceID: string,
  param: SpaceCalendarParam
): SpaceCalendar {
  const response = useSWR(
    `/api/bookings/space_calendar${toSpaceCalendarSearchParams(
      spaceID,
      param
    )}`,
    { suspense: true }
  );

  return toSpaceCalendar(response.data);
}
export interface PhysicalSpace {
  id: string;
  floor: string;
  room: string;
}

function toPhysicalSpace(data: any): PhysicalSpace {
  return {
    id: data.id,
    floor: data.floor,
    room: data.name,
  };
}

export function useGetSpacePhysicalSpaces(spaceID: string): PhysicalSpace[] {
  const response = useSWR(
    () => (spaceID ? `/api/spaces/${spaceID}/physical_spaces` : null),
    {
      suspense: true,
    }
  );

  if (!response.data) {
    return [];
  }

  return response.data.map(toPhysicalSpace);
}

interface DailyBookingInput {
  spaceID: string;
  physicalSpaceID?: string;
  bookingType: BookingType.Daily;
  layoutID?: string;
  startDate: string;
  attendees?: number;
  endDate: string;
  time?: string | null;
}

interface HourlyBookingInput {
  spaceID: string;
  physicalSpaceID?: string;
  bookingType: BookingType.Hourly;
  layoutID?: string;
  date: string;
  time: string;
  attendees?: number;
  duration: number;
}

interface OrderItemBookingInput {
  orderableID?: string;
  orderableType: OrderableType.Booking;
  details: HourlyBookingInput | DailyBookingInput;
}

export type OrderItemInput = OrderItemBookingInput;

interface OrderInput {
  id?: string;
  additionalNotes: string;
  confirmBookingWoPayment?: boolean;
  guestFullname: string;
  guestCompanyName: string;
  guestPhone: string;
  guestEmail: string;
  source?: "Marketplace" | "WL" | "FM";
  orderItems: OrderItemInput[];
  partnerID?: string;
}

interface CheckQuoteInput {
  orderItems: OrderItemInput[];
}

interface BookingsParam {
  startDate: string;
  endDate: string;
}

interface SearchBookingsParams extends BookingsParam {
  spaceID: string;
}

function toBookingSearchParams(param: SearchBookingsParams) {
  const searchParams = new URLSearchParams();

  searchParams.set("start_date", param.startDate);
  searchParams.set("end_date", param.endDate);
  searchParams.set("space_id", param.spaceID);

  return `?${searchParams.toString()}`;
}

export function useGetSpaceBookings(
  spaceID: string,
  param: BookingsParam
): Booking[] {
  const response = useSWR(
    param
      ? `/api/v2/bookings${toBookingSearchParams({ ...param, spaceID })}`
      : null,
    { suspense: true }
  );

  return response.data.map(toBooking);
}

async function fetchSpacesBookings(
  spaceIDs: string[],
  param: BookingsParam,
  token: string
) {
  let bookings: Booking[] = [];

  await Promise.all(
    spaceIDs.map(async (spaceID) => {
      const res = await fetch(
        `/api/v2/bookings${toBookingSearchParams({ ...param, spaceID })}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const body = await res.json();
      bookings = bookings.concat(body.map(toBooking));
    })
  );

  return bookings;
}

export function useGetSpacesBookings(
  spaceIDs: string[],
  param: BookingsParam
): Booking[] {
  const { token } = useAuth0();
  const keys = spaceIDs.join("");
  const response = useSWR(
    `/api/v2/bookings?spaceIDs=${keys}`,
    () => fetchSpacesBookings(spaceIDs, param, token),
    { suspense: true }
  );

  return response.data as Booking[];
}

function fromOrderItemInput(input: OrderItemInput) {
  if (input.orderableType === OrderableType.Booking) {
    if (input.details.bookingType === BookingType.Daily) {
      return {
        id: input.orderableID,
        orderable_type: input.orderableType,
        type: input.orderableType,
        details: {
          physical_space_id: input.details.physicalSpaceID,
          attendees: input.details.attendees,
          space_id: input.details.spaceID,
          space_layout_id: input.details.layoutID,
          start_date: input.details.startDate,
          end_date: input.details.endDate,
          time: input.details.time,
        },
      };
    }

    return {
      id: input.orderableID,
      orderable_type: input.orderableType,
      type: input.orderableType,
      details: {
        physical_space_id: input.details.physicalSpaceID,
        attendees: input.details.attendees,
        space_id: input.details.spaceID,
        space_layout_id: input.details.layoutID,
        date: input.details.date,
        time: input.details.time,
        duration: input.details.duration,
      },
    };
  }
}

function fromOrderInput(input: OrderInput) {
  return {
    id: input.id,
    fullname: input.guestFullname,
    company_name: input.guestCompanyName,
    phone: input.guestPhone,
    source: input.source,
    email: input.guestEmail,
    confirm_booking_wo_payment: input.confirmBookingWoPayment,
    additional_notes: input.additionalNotes,
    order_items: input.orderItems.map(fromOrderItemInput),
    partner_id: input.partnerID,
  };
}

function fromCheckQuoteInput(input: CheckQuoteInput) {
  return {
    order_items: input.orderItems.map(fromOrderItemInput),
  };
}

function toBooking(data: any): Booking {
  const duration =
    data.end_time && data.start_time
      ? String(getDurationInHours(data.end_time, data.start_time))
      : data.duration;

  return {
    id: data.id,
    attendees: data.attendees,
    spaceID: data.space_id,
    spaceType: data.space_type,
    bookingType: data.booking_type,
    layoutID: data.space_layout_id,
    date: data.date,
    time: data.time || data.start_time,
    duration: duration,
    startDate: data.start_date,
    endDate: data.end_date,
    room: data.name,
    days: data.days || data.duration,
    floor: data.floor,
    orderID: data.order_id,
    originalOrderId: data.original_order_id,
    orderConfirmationID: data.order_confirmation_id,
    orderCreatedAt: new Date(data.order_created_at * 1000), // unix timestamp in SECONDS
    orderName: data.order_fullname,
    orderEmail: data.order_email,
    physicalSpaceID: data.physical_space_id,
    cancellationAllowed: data.cancellation_allowed,
    cancellationAllowedBefore: data.cancellation_allowed_before
      ? // unix timestamp in SECONDS
        new Date(data.cancellation_allowed_before * 1000)
      : null,
  };
}

function toOrderItem(data: any): OrderItem {
  const orderableType: OrderableType.Booking = data.orderable_type || data.type;

  return {
    orderableType,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    id: data.id,
    details: toBooking(data.details),
  };
}

function toQuoteOrderItem(data: any): QuoteOrderItem {
  const orderableType: OrderableType.Booking = data.orderable_type || data.type;

  return {
    orderableType,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    details: {
      attendees: data.details.attendees,
      date: data.details.date,
      duration: data.details.duration,
      spaceID: data.details.space_id,
      time: data.details.time,
      startDate: data.details.start_date,
      endDate: data.details.end_date,
      days: data.details.days,
    },
  };
}

function toQuote(data: any): Quote {
  return {
    totalPrice: data.total_price,
    orderItems: data.order_items.map(toQuoteOrderItem),
  };
}

function toPaymentMethod(data: any): PaymentMethod | null {
  if (!data || !data.card) {
    return null;
  }

  return {
    card: {
      last4: data.card.last4,
      network: data.card.network,
      networkLogoUrl: data.card.network_logo_url,
    },
  };
}

function toOrder(data: any): Order {
  return {
    id: data.id,
    confirmationID: data.confirmation_id,
    status: data.status,
    currencyCode: data.currency,
    stripeClientSecret: data.stripe_client_secret,
    totalPrice: data.total_price,
    additionalNotes: data.additional_notes,
    guestName: data.fullname,
    guestEmail: data.email,
    guestCompanyName: data.company_name,
    guestPhoneNumber: data.phone,
    orderItems: data.order_items.map(toOrderItem),
    paymentMethodDetails: toPaymentMethod(data.payment_method_details),
    createdAt: new Date(data.created_at * 1000),
  };
}

export function useCreateOrder() {
  const post = usePost();

  return async (input: OrderInput): Promise<Order> => {
    const data = fromOrderInput(input);

    const response = await post("/api/v2/orders", data);

    return toOrder(response);
  };
}

export interface BlockOutDates {
  id: string;
  spaceID: string;
  startDate: string;
  endDate: string;
  reason: string;
}

function toBlockOutDates(data: any): BlockOutDates {
  return {
    id: data.id,
    spaceID: data.space_id,
    startDate: data.start_date,
    endDate: data.end_date,
    reason: data.reason,
  };
}

interface DateRangeParams {
  startDate: string;
  endDate: string;
}

function toSpaceDateRange(
  spaceID: string,
  param: DateRangeParams & { capacity?: number; physicalSpaceID?: string }
) {
  const searchParams = new URLSearchParams();

  searchParams.set("space_id", spaceID);

  searchParams.set("start_date", param.startDate);
  searchParams.set("end_date", param.endDate);

  if (param.capacity) {
    searchParams.set("capacity", `${param.capacity}`);
  }
  if (param.physicalSpaceID) {
    searchParams.set("physical_space_id", `${param.physicalSpaceID}`);
  }

  return `?${searchParams.toString()}`;
}

export function useGetSpaceBlockOutDates(
  spaceID: string,
  param: {
    startDate: string;
    endDate: string;
  }
): BlockOutDates[] {
  const response = useSWR(
    () =>
      spaceID
        ? `/api/partners/bookings/block_out_dates${toSpaceDateRange(
            spaceID,
            param
          )}`
        : null,
    {
      suspense: true,
    }
  );

  if (!response.data) {
    return [];
  }

  return response.data.map(toBlockOutDates);
}

export function useCreatePartnerOrder() {
  const post = usePost();

  return async (input: OrderInput): Promise<Order> => {
    const data = fromOrderInput({
      ...input,
      source: "FM",
    });

    const response = await post("/api/v2/partners/orders", data);

    return toOrder(response);
  };
}

export async function invalidateSpaceCache(spaceID: string) {
  const keys = cache.keys();
  // Delete any cache that includes the spaceID in its key
  const spaceKeys = keys.filter((k) => k.includes(spaceID));

  for (let k of spaceKeys) {
    await mutate(k);
  }
}

export function useInvalidateSpaceCache() {
  return async (spaceID: string): Promise<void> => {
    await invalidateSpaceCache(spaceID);

    return;
  };
}

export function useCancelBooking() {
  const post = usePost();

  return async (bookingID: string): Promise<void> => {
    await post(`/api/v2/bookings/${bookingID}/cancel`);

    return;
  };
}

export function useUpdateOrder() {
  const put = usePut();

  return async (input: OrderInput): Promise<Order> => {
    const data = fromOrderInput(input);

    const response = await put(`/api/orders/${data.id}`, data);

    return toOrder(response);
  };
}

export function useUpdatePartnerOrder() {
  const put = usePut();

  return async (input: OrderInput): Promise<Order> => {
    const data = fromOrderInput(input);

    const response = await put(`/api/v2/partners/orders/${data.id}`, data);

    return toOrder(response);
  };
}

export function useGetOrderCallback() {
  const get = useGet();

  return async (orderID: string): Promise<Order> => {
    const response = await get(`/api/v2/orders/${orderID}`);

    return toOrder(response);
  };
}

export function useGetPartnerOrderCallback() {
  const get = useGet();

  return async (orderID: string): Promise<Order> => {
    const response = await get(`/api/v2/partners/orders/${orderID}`);

    return toOrder(response);
  };
}

export function useGetOrder(orderID: string): Order {
  const response = useSWR(`/api/v2/orders/${orderID}`, { suspense: true });

  return toOrder(response.data);
}

export function useGetPartnerOrder(orderID: string): Order {
  const response = useSWR(`/api/v2/partners/orders/${orderID}`, {
    suspense: true,
  });

  return toOrder(response.data);
}

export function useGetLocationsByPartnerOfferingSlug(
  slug: string
): Array<Location> {
  let searchParams = new URLSearchParams();
  let qs: string;
  const { locale } = useI18n();
  searchParams.set("locale", locale.languageTag);
  qs = `?${searchParams.toString()}`;

  const response = useSWR(`/api/v2/partner_offerings/${slug}/locations${qs}`, {
    suspense: true,
  });

  return response.data.map(toLocation);
}

export function useCheckQuote() {
  const post = usePost();

  return useCallback(
    async (input: CheckQuoteInput): Promise<Quote> => {
      const data = fromCheckQuoteInput(input);

      const response = await post("/api/v2/quotes", data);

      return toQuote(response);
    },
    [post]
  );
}

interface CheckPurchaseStatusInputs {
  email: string;
  partnerId: string;
}

export function useCheckPurchaseStatus(inputs: CheckPurchaseStatusInputs) {
  const { email, partnerId } = inputs;
  const response = useSWR<PurchaseStatusResult>(
    `/api/v2/partners/${partnerId}/purchase_status/?email=${encodeURIComponent(
      email
    )}`,
    {
      suspense: true,
    }
  );

  const {
    has_portal_account: hasPortalAccount = false,
    purchased_membership_portal: purchasedMembershipPortal = false,
    purchased_membership_wo_portal: purchasedMembershipWoPortal = false,
    purchased_multi_pass: purchasedMultiPass = false,
  } = response.data || {};

  return {
    hasPortalAccount,
    purchasedMembershipPortal,
    purchasedMembershipWoPortal,
    purchasedMultiPass,
  };
}

function usePost() {
  const { token } = useAuth0();

  return async (url: string, data?: any) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const body = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return body;
    }

    throw new APIError(body.message, body.code);
  };
}

function useGet() {
  const { token } = useAuth0();

  return async (url: string) => {
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const body = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return body;
    }

    throw new APIError(body.message);
  };
}

function usePut() {
  const { token } = useAuth0();
  return async (url: string, data?: any) => {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    const body = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return body;
    }

    throw new APIError(body.message);
  };
}
